import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, CheckCircle2, Phone, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BiLabel = ({ en, mr }) => (
  <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
    {en} <span className="text-gray-400 font-normal">/ {mr}</span>
  </label>
);

const AuthPage = () => {
  const { login, signup } = useAuth();
  const [view, setView] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('phone');
  const [forgotMsg, setForgotMsg] = useState('');

  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    shop_name: '', shop_place: '', email: '', phone: '', password: ''
  });
  const [signupMethod, setSignupMethod] = useState('phone');
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotMethod, setForgotMethod] = useState('phone');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginForm.identifier, loginForm.password);
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!signupForm.email && !signupForm.phone) {
      setError('Please provide email or phone number');
      return;
    }
    setLoading(true);
    try {
      await signup({ ...signupForm, first_name: signupForm.shop_name, last_name: signupForm.shop_place });
      setView('success');
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setForgotMsg('');
    if (forgotMethod === 'phone') { setForgotMsg('contact_admin'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { identifier: forgotIdentifier, method: 'email' });
      setForgotMsg('email_sent');
    } catch { setError('Something went wrong.'); }
    finally { setLoading(false); }
  };

  const switchView = (v) => { setView(v); setError(''); setForgotMsg(''); };
  const inputCls = "w-full bg-gray-50 border border-gray-200 focus:border-[#1A3626] focus:bg-white focus:ring-1 focus:ring-[#1A3626]/20 rounded-xl px-4 py-3 text-sm transition-all outline-none";

  if (view === 'success') {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 max-w-md w-full text-center" data-testid="signup-success">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-[#1A3626] mb-2">Your account request has been submitted.</h2>
          <p className="text-sm text-gray-500 mb-6">Access will be granted after admin approval.</p>
          <button onClick={() => switchView('login')} className="text-sm font-medium text-[#C05A3E] hover:text-[#A64B31]" data-testid="back-to-login-btn">Back to Login</button>
        </div>
      </div>
    );
  }

  if (view === 'forgot') {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6"><img src="/logo-new.png" alt="Krishna Machinery Stores" className="h-20 mx-auto object-contain" /></div>
          <div className="bg-white rounded-2xl shadow-lg p-7 sm:p-8" data-testid="forgot-card">
            <button onClick={() => switchView('login')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1A3626] mb-4"><ArrowLeft className="h-4 w-4" /> Back to Login</button>
            <h2 className="text-lg font-bold text-[#1A3626] mb-1">Reset Password</h2>
            <p className="text-xs text-gray-400 mb-5">Choose how you'd like to reset your password.</p>
            <div className="flex bg-gray-100 rounded-lg p-1 mb-5">
              <button onClick={() => { setForgotMethod('phone'); setForgotMsg(''); setError(''); }} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-md transition-all ${forgotMethod === 'phone' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-500'}`}><Phone className="h-3.5 w-3.5" /> Phone</button>
              <button onClick={() => { setForgotMethod('email'); setForgotMsg(''); setError(''); }} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-md transition-all ${forgotMethod === 'email' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-500'}`}><Mail className="h-3.5 w-3.5" /> Email</button>
            </div>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg mb-4">{error}</div>}
            {forgotMsg === 'contact_admin' && <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-4 py-3 rounded-lg mb-4">Please contact the admin to reset your password.</div>}
            {forgotMsg === 'email_sent' && <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg mb-4">If an account exists, a reset link has been sent.</div>}
            {forgotMethod === 'phone' && !forgotMsg && <button onClick={() => setForgotMsg('contact_admin')} className="w-full bg-[#1A3626] text-white rounded-xl py-3 text-sm font-semibold hover:bg-[#2a5440] transition-colors">Reset via Phone</button>}
            {forgotMethod === 'email' && !forgotMsg && (
              <form onSubmit={handleForgot} className="space-y-4">
                <div><BiLabel en="Email Address" mr="ईमेल" /><input type="email" required value={forgotIdentifier} onChange={(e) => setForgotIdentifier(e.target.value)} placeholder="Enter your email" className={inputCls} /></div>
                <button type="submit" disabled={loading} className="w-full bg-[#1A3626] text-white rounded-xl py-3 text-sm font-semibold hover:bg-[#2a5440] transition-colors disabled:opacity-50">{loading ? 'Sending...' : 'Send Reset Link'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6"><img src="/logo-new.png" alt="Krishna Machinery Stores" className="h-20 mx-auto object-contain" /></div>
        <div className="bg-white rounded-2xl shadow-lg p-7 sm:p-8" data-testid="auth-card">
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button onClick={() => switchView('login')} className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${view === 'login' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-500'}`} data-testid="login-tab">Login</button>
            <button onClick={() => switchView('signup')} className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${view === 'signup' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-500'}`} data-testid="signup-tab">Sign Up</button>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg mb-4" data-testid="auth-error">{error}</div>}

          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div className="flex bg-gray-100 rounded-lg p-0.5 mb-1">
                <button type="button" onClick={() => { setLoginMethod('phone'); setLoginForm({ ...loginForm, identifier: '' }); }} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${loginMethod === 'phone' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-400'}`}><Phone className="h-3.5 w-3.5" /> Phone</button>
                <button type="button" onClick={() => { setLoginMethod('email'); setLoginForm({ ...loginForm, identifier: '' }); }} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${loginMethod === 'email' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-400'}`}><Mail className="h-3.5 w-3.5" /> Email</button>
              </div>
              <div>
                <BiLabel en={loginMethod === 'phone' ? 'Contact Number' : 'Email Address'} mr={loginMethod === 'phone' ? 'संपर्क क्रमांक' : 'ईमेल'} />
                <input type={loginMethod === 'phone' ? 'tel' : 'email'} required maxLength={loginMethod === 'phone' ? 10 : undefined}
                  value={loginForm.identifier} onChange={(e) => setLoginForm({ ...loginForm, identifier: loginMethod === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value })}
                  placeholder={loginMethod === 'phone' ? 'Enter 10-digit phone' : 'Enter email'} className={inputCls} data-testid="login-identifier" />
              </div>
              <div>
                <BiLabel en="Password" mr="पासवर्ड" />
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="Enter password" className={`${inputCls} pr-11`} data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-[#1A3626] text-white rounded-xl py-3 text-sm font-semibold hover:bg-[#2a5440] transition-colors disabled:opacity-50" data-testid="login-submit">{loading ? 'Signing in...' : 'Sign In'}</button>
              <button type="button" onClick={() => switchView('forgot')} className="w-full text-xs text-gray-400 hover:text-[#C05A3E] transition-colors" data-testid="forgot-link">Forgot Password?</button>
            </form>
          )}

          {view === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-3.5" data-testid="signup-form">
              <div>
                <BiLabel en="Shop Name" mr="शॉप चे नाव" />
                <input type="text" required value={signupForm.shop_name} onChange={(e) => setSignupForm({ ...signupForm, shop_name: e.target.value })} placeholder="Enter shop name" className={inputCls} data-testid="signup-first-name" />
              </div>
              <div>
                <BiLabel en="Shop Place" mr="शॉप चे गाव" />
                <input type="text" required value={signupForm.shop_place} onChange={(e) => setSignupForm({ ...signupForm, shop_place: e.target.value })} placeholder="Enter shop place" className={inputCls} data-testid="signup-last-name" />
              </div>

              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button type="button" onClick={() => { setSignupMethod('phone'); setSignupForm({ ...signupForm, email: '' }); }} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${signupMethod === 'phone' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-400'}`}><Phone className="h-3.5 w-3.5" /> Phone</button>
                <button type="button" onClick={() => { setSignupMethod('email'); setSignupForm({ ...signupForm, phone: '' }); }} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${signupMethod === 'email' ? 'bg-white text-[#1A3626] shadow-sm' : 'text-gray-400'}`}><Mail className="h-3.5 w-3.5" /> Email</button>
              </div>

              {signupMethod === 'phone' ? (
                <div><BiLabel en="Contact Number" mr="संपर्क क्रमांक" /><input type="tel" maxLength={10} value={signupForm.phone} onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="Enter 10-digit phone" className={inputCls} data-testid="signup-phone" /></div>
              ) : (
                <div><BiLabel en="Email Address" mr="ईमेल" /><input type="email" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} placeholder="Enter email" className={inputCls} data-testid="signup-email" /></div>
              )}

              <div>
                <BiLabel en="Password" mr="पासवर्ड" />
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} required value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} placeholder="Create password" className={`${inputCls} pr-11`} data-testid="signup-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">You can use numbers, letters, or a mix as your password.</p>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-[#C05A3E] text-white rounded-xl py-3 text-sm font-semibold hover:bg-[#A64B31] transition-colors disabled:opacity-50" data-testid="signup-submit">{loading ? 'Submitting...' : 'Create Account'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
